import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, Grid, TextField, Button, Avatar, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, IconButton, Dialog, ToggleButton, ToggleButtonGroup,
  Chip, Tooltip
} from "@mui/material";
import {
  Save as SaveIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  CalendarMonth as CalendarIcon,
  Today as TodayIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as PresentIcon,
  Cancel as AbsentIcon,
  Update as HalfDayIcon,
  Print as PrintIcon
} from "@mui/icons-material";
import { auth, db } from "../../firebase";
import { collection, query, where, getDocs, doc, getDoc, writeBatch, serverTimestamp } from "firebase/firestore";
import Swal from "sweetalert2";

const PRIMARY = "#0d9488";
const BG_LIGHT = "#f1f5f9";

function Teacherattendance() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [schoolId, setSchoolId] = useState(null);
  const [teachers, setTeachers] = useState([]);
  
  // View Modes
  const [viewMode, setViewMode] = useState("daily"); // 'daily' | 'monthly'
  
  // Daily State
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [dailyAttendance, setDailyAttendance] = useState({}); 
  
  // Monthly State
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [monthlyData, setMonthlyData] = useState({});
  
  // Image Preview Modal
  const [imagePreview, setImagePreview] = useState(null);

  // 1. Initialize School & Teachers
  useEffect(() => {
    const init = async () => {
      try {
        if (!auth.currentUser) return;
        const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
        if (userSnap.exists()) {
          const sId = userSnap.data().schoolId;
          setSchoolId(sId);
          await fetchTeachers(sId);
        }
      } catch (err) {
        console.error("Init Error:", err);
      }
    };
    init();
  }, []);

  const fetchTeachers = async (sId) => {
    try {
      const q = query(collection(db, "users"), where("schoolId", "==", sId), where("role", "==", "Teacher"));
      const snap = await getDocs(q);
      const tList = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setTeachers(tList);
    } catch (err) {
      console.error("Error fetching teachers:", err);
    }
  };

  // 2. Fetch Data Based on View Mode
  useEffect(() => {
    if (!schoolId || teachers.length === 0) {
      if (teachers.length === 0) setLoading(false);
      return;
    }
    if (viewMode === "daily") fetchDailyAttendance();
    else fetchMonthlyAttendance();
  }, [schoolId, teachers, selectedDate, selectedMonth, viewMode]);

  const fetchDailyAttendance = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "attendance"), where("date", "==", selectedDate));
      const snap = await getDocs(q);
      
      const recordMap = {};
      snap.forEach(doc => {
        const data = doc.data();
        if (data.staffId) recordMap[data.staffId] = data; 
      });

      const currentState = {};
      teachers.forEach(t => {
        if (recordMap[t.id]) {
          currentState[t.id] = recordMap[t.id];
        } else {
          // DEFAULT TO ABSENT IF NOT MARKED
          currentState[t.id] = { status: "Absent", isSynthetic: true };
        }
      });
      
      setDailyAttendance(currentState);
    } catch (err) {
      console.error("Error fetching daily attendance:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyAttendance = async () => {
    setLoading(true);
    try {
      const startOfMonth = `${selectedMonth}-01`;
      const [year, month] = selectedMonth.split('-');
      const lastDay = new Date(year, month, 0).getDate();
      const endOfMonth = `${selectedMonth}-${lastDay}`;

      const q = query(
        collection(db, "attendance"),
        where("date", ">=", startOfMonth),
        where("date", "<=", endOfMonth)
      );
      
      const snap = await getDocs(q);
      const monthMap = {};
      teachers.forEach(t => { monthMap[t.id] = {}; });

      snap.forEach(doc => {
        const data = doc.data();
        if (data.staffId && monthMap[data.staffId]) {
          monthMap[data.staffId][data.date] = data;
        }
      });

      setMonthlyData(monthMap);
    } catch (err) {
      console.error("Error fetching monthly:", err);
    } finally {
      setLoading(false);
    }
  };

  // 3. Handlers for Admin Overrides
  const handleMarkDaily = (teacherId, status) => {
    setDailyAttendance(prev => ({
      ...prev,
      [teacherId]: {
        ...prev[teacherId],
        status: status,
        isSynthetic: false,
        isManualOverride: true 
      }
    }));
  };

  const saveDailyAttendance = async () => {
    setSaving(true);
    try {
      const batch = writeBatch(db);
      
      Object.keys(dailyAttendance).forEach(teacherId => {
        const record = dailyAttendance[teacherId];
        
        if (record && record.isManualOverride) {
          const docId = `${teacherId}_${selectedDate}`;
          const ref = doc(db, "attendance", docId);
          const teacherName = teachers.find(t => t.id === teacherId)?.name || "Unknown";

          batch.set(ref, {
            staffId: teacherId,
            staffName: teacherName,
            schoolId: schoolId,
            date: selectedDate,
            status: record.status,
            updatedAt: serverTimestamp(),
            markedByAdmin: auth.currentUser.uid
          }, { merge: true });
        }
      });

      await batch.commit();
      Swal.fire("Saved", "Attendance overrides recorded successfully.", "success");
      fetchDailyAttendance(); 
    } catch (error) {
      console.error("Error saving attendance:", error);
      Swal.fire("Error", "Failed to save attendance.", "error");
    } finally {
      setSaving(false);
    }
  };

  // 4. Formatting & Calculation Helpers
  const formatTime = (isoString) => {
    if (!isoString) return "--:--";
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getDuration = (inTime, outTime) => {
    if (!inTime || !outTime) return "-";
    const diff = new Date(outTime) - new Date(inTime);
    if (diff < 0) return "-";
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${mins}m`;
  };

  const getDaysArray = () => {
    if (!selectedMonth) return [];
    const [year, month] = selectedMonth.split('-');
    const daysInMonth = new Date(year, month, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => `${selectedMonth}-${(i + 1).toString().padStart(2, '0')}`);
  };

  const getStatusConfig = (status) => {
    if (status === "Present") return { color: "success", hex: "#10b981", bg: "#dcfce7", label: "P" };
    if (status === "Half Day") return { color: "warning", hex: "#f59e0b", bg: "#fef3c7", label: "HD" };
    if (status === "Absent") return { color: "error", hex: "#ef4444", bg: "#fee2e2", label: "A" };
    return { color: "default", hex: "#64748b", bg: "#f1f5f9", label: "-" };
  };

  const stats = {
    total: teachers.length,
    present: Object.values(dailyAttendance).filter(v => v?.status === "Present").length,
    absent: Object.values(dailyAttendance).filter(v => v?.status === "Absent").length,
    halfDay: Object.values(dailyAttendance).filter(v => v?.status === "Half Day").length,
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Box p={2} sx={{ 
      bgcolor: BG_LIGHT, 
      minHeight: "100vh",
      // Print Styles
      '@media print': {
        bgcolor: 'white',
        p: 0,
        '& .no-print': { display: 'none !important' },
        '& .print-only': { display: 'block !important' },
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact',
      }
    }}>
      
      {/* HEADER & CONTROLS */}
      <Paper className="no-print" elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, border: "1px solid #e2e8f0", background: "linear-gradient(to right, #ffffff, #f8fafc)" }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
          <Box>
            <Typography variant="h5" fontWeight="900" color="#0f172a" sx={{ letterSpacing: '-0.5px' }}>Staff Attendance</Typography>
            <Typography variant="body2" color="text.secondary" fontWeight="500">Monitor daily punches and monthly timesheets</Typography>
          </Box>

          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            {/* View Mode Toggle */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, val) => val && setViewMode(val)}
              size="small"
              sx={{ bgcolor: "#fff", boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
            >
              <ToggleButton value="daily" sx={{ px: 2, textTransform: 'none', fontWeight: 600 }}><TodayIcon sx={{ mr: 1, fontSize: 18 }} /> Daily View</ToggleButton>
              <ToggleButton value="monthly" sx={{ px: 2, textTransform: 'none', fontWeight: 600 }}><CalendarIcon sx={{ mr: 1, fontSize: 18 }} /> Monthly View</ToggleButton>
            </ToggleButtonGroup>

            {/* Date / Month Picker */}
            {viewMode === "daily" ? (
              <TextField
                type="date"
                size="small"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                sx={{ bgcolor: "#fff", width: 150, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            ) : (
              <TextField
                type="month"
                size="small"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                sx={{ bgcolor: "#fff", width: 160, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            )}

            {/* Print Button */}
            {/* <Button
              variant="outlined"
              onClick={handlePrint}
              startIcon={<PrintIcon />}
              sx={{ bgcolor: "#fff", borderColor: "#cbd5e1", color: "#475569", textTransform: "none", fontWeight: "bold" }}
            >
              Print
            </Button> */}
          </Stack>
        </Stack>
      </Paper>

      {/* Print-Only Title Header */}
      <Box className="print-only" sx={{ display: 'none', mb: 3, textAlign: 'center' }}>
        <Typography variant="h4" fontWeight="bold">Staff Attendance Report</Typography>
        <Typography variant="subtitle1">
          {viewMode === "daily" ? `Date: ${selectedDate}` : `Month: ${selectedMonth}`}
        </Typography>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={10}>
          <CircularProgress sx={{ color: PRIMARY }} />
        </Box>
      ) : viewMode === "daily" ? (
        
        /* ---------------- DAILY VIEW ---------------- */
        <Box>
          {/* STATS SUMMARY */}
          <Grid className="row" spacing={2} mb={3}>
            {[
              { label: "Total Staff", val: stats.total, color: "#3b82f6", bg: "#eff6ff" },
              { label: "Present", val: stats.present, color: "#10b981", bg: "#ecfdf5" },
              { label: "Half Day", val: stats.halfDay, color: "#f59e0b", bg: "#fffbeb" },
              { label: "Absent", val: stats.absent, color: "#ef4444", bg: "#fef2f2" },
            ].map((stat, i) => (
              <Grid className="col-sm-3" item xs={6} sm={3} key={i}>
                <Paper sx={{ p: 2, borderRadius: 3, bgcolor: stat.bg, border: `1px solid ${stat.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: 'none' }} elevation={0}>
                  <Box>
                    <Typography variant="caption" fontWeight="800" sx={{ color: stat.color, textTransform: 'uppercase', letterSpacing: 1 }}>{stat.label}</Typography>
                    <Typography variant="h4" fontWeight="900" sx={{ color: stat.color, mt: 0.5, lineHeight: 1 }}>{stat.val}</Typography>
                  </Box>
                  <Avatar className="no-print" sx={{ bgcolor: `${stat.color}20`, color: stat.color }}>
                    {stat.label === "Total Staff" ? <TodayIcon /> : stat.label === "Present" ? <PresentIcon /> : stat.label === "Half Day" ? <HalfDayIcon /> : <AbsentIcon />}
                  </Avatar>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden", bgcolor: '#fff', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', '@media print': { border: 'none', boxShadow: 'none' } }}>
            
            {/* TEACHER LIST */}
            <TableContainer >
              <Table size="medium" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: "#f8fafc", fontWeight: "800", color: '#475569' }}>Teacher</TableCell>
                    <TableCell align="center" sx={{ bgcolor: "#f8fafc", fontWeight: "800", color: '#475569' }}>Check In</TableCell>
                    <TableCell align="center" sx={{ bgcolor: "#f8fafc", fontWeight: "800", color: '#475569' }}>Check Out</TableCell>
                    <TableCell align="center" sx={{ bgcolor: "#f8fafc", fontWeight: "800", color: '#475569' }}>Work Hours</TableCell>
                    <TableCell align="center" sx={{ bgcolor: "#f8fafc", fontWeight: "800", color: '#475569' }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {teachers.map((teacher) => {
                    const record = dailyAttendance[teacher.id];
                    const status = record?.status || "Absent";
                    const duration = getDuration(record?.checkInTime, record?.checkOutTime);

                    return (
                      <TableRow key={teacher.id} hover sx={{ '& td': { borderBottom: '1px solid #f1f5f9' } }}>
                        {/* 1. STAFF INFO */}
                        <TableCell>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar src={teacher.photoURL} sx={{ bgcolor: PRIMARY, width: 42, height: 42, fontWeight: 'bold' }}>
                              {teacher.name?.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" fontWeight="800" color="#0f172a">{teacher.name}</Typography>
                              {/* <Typography variant="caption" color="text.secondary" fontWeight="500">ID: {teacher.id.slice(-5).toUpperCase()}</Typography> */}
                            </Box>
                          </Stack>
                        </TableCell>

                        {/* 2. CHECK IN */}
                        <TableCell align="center">
                          {record?.checkInTime ? (
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                              <Chip size="small" icon={<AccessTimeIcon style={{ fontSize: 14 }} className="no-print" />} label={formatTime(record.checkInTime)} sx={{ bgcolor: '#f1f5f9', fontWeight: 600 }} />
                              {record.checkInImage && (
                                <Tooltip title="View Snapshot">
                                  <Avatar 
                                    className="no-print"
                                    src={record.checkInImage} variant="rounded" 
                                    sx={{ width: 28, height: 28, cursor: 'pointer', border: '1px solid #e2e8f0', '&:hover': { opacity: 0.8 } }}
                                    onClick={() => setImagePreview(record.checkInImage)}
                                  />
                                </Tooltip>
                              )}
                            </Stack>
                          ) : <Typography variant="caption" color="text.disabled" fontWeight="600">--:--</Typography>}
                        </TableCell>

                        {/* 3. CHECK OUT */}
                        <TableCell align="center">
                          {record?.checkOutTime ? (
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                              <Chip size="small" icon={<AccessTimeIcon style={{ fontSize: 14 }} className="no-print" />} label={formatTime(record.checkOutTime)} sx={{ bgcolor: '#f1f5f9', fontWeight: 600 }} />
                              {record.checkOutImage && (
                                <Tooltip title="View Snapshot">
                                  <Avatar 
                                    className="no-print"
                                    src={record.checkOutImage} variant="rounded" 
                                    sx={{ width: 28, height: 28, cursor: 'pointer', border: '1px solid #e2e8f0', '&:hover': { opacity: 0.8 } }}
                                    onClick={() => setImagePreview(record.checkOutImage)}
                                  />
                                </Tooltip>
                              )}
                            </Stack>
                          ) : <Typography variant="caption" color="text.disabled" fontWeight="600">--:--</Typography>}
                        </TableCell>

                        {/* 4. WORK HOURS */}
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="800" sx={{ color: duration !== "-" ? PRIMARY : 'text.disabled' }}>
                            {duration}
                          </Typography>
                        </TableCell>

                        {/* 5. STATUS OVERRIDE (Buttons for screen, Text for Print) */}
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center" className="no-print" sx={{ bgcolor: '#f8fafc', p: 0.5, borderRadius: 2, display: 'inline-flex' }}>
                            <Button 
                              variant={status === "Present" ? "contained" : "text"} 
                              color="success" size="small"
                              onClick={() => handleMarkDaily(teacher.id, "Present")}
                              sx={{ borderRadius: 1.5, minWidth: 40, px: 1, py: 0.5, fontWeight: 'bold' }}
                            >P</Button>
                            <Button 
                              variant={status === "Half Day" ? "contained" : "text"} 
                              color="warning" size="small"
                              onClick={() => handleMarkDaily(teacher.id, "Half Day")}
                              sx={{ borderRadius: 1.5, minWidth: 40, px: 1, py: 0.5, fontWeight: 'bold' }}
                            >HD</Button>
                            <Button 
                              variant={status === "Absent" ? "contained" : "text"} 
                              color="error" size="small"
                              onClick={() => handleMarkDaily(teacher.id, "Absent")}
                              sx={{ borderRadius: 1.5, minWidth: 40, px: 1, py: 0.5, fontWeight: 'bold' }}
                            >A</Button>
                          </Stack>

                          {/* Render simple text exclusively for printing */}
                          <Typography className="print-only" sx={{ display: 'none', fontWeight: 'bold', color: getStatusConfig(status).hex }}>
                            {status}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* SAVE BUTTON */}
            <Box className="no-print" p={2} sx={{ bgcolor: "#f8fafc", borderTop: "1px solid #e2e8f0", textAlign: "right" }}>
              <Button
                variant="contained"
                onClick={saveDailyAttendance}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon fontSize="small" />}
                sx={{ bgcolor: PRIMARY, px: 4, py: 1, borderRadius: 2, fontWeight: 'bold', textTransform: 'none' }}
              >
                Save Overrides
              </Button>
            </Box>
          </Paper>
        </Box>

      ) : (

        /* ---------------- MONTHLY VIEW ---------------- */
        <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', '@media print': { border: 'none', boxShadow: 'none' } }}>
          <TableContainer sx={{ maxHeight: '70vh', '@media print': { maxHeight: 'none', overflow: 'visible' } }}>
            <Table size="small" stickyHeader sx={{ '& th, & td': { borderBottom: '1px solid #e2e8f0' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: "#f8fafc", fontWeight: "800", minWidth: 200, position: 'sticky', left: 0, zIndex: 3 }}>
                    Staff Member
                  </TableCell>
                  <TableCell align="center" sx={{ bgcolor: "#ecfdf5", color: "#10b981", fontWeight: "900", minWidth: 50 }}>P</TableCell>
                  <TableCell align="center" sx={{ bgcolor: "#fef2f2", color: "#ef4444", fontWeight: "900", minWidth: 50 }}>A</TableCell>
                  <TableCell align="center" sx={{ bgcolor: "#fffbeb", color: "#f59e0b", fontWeight: "900", minWidth: 50 }}>HD</TableCell>
                  {getDaysArray().map(date => (
                    <TableCell key={date} align="center" sx={{ bgcolor: "#f8fafc", fontWeight: "700", fontSize: '0.7rem', minWidth: 40, borderLeft: '1px solid #e2e8f0' }}>
                      {date.split('-')[2]}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {teachers.map(teacher => {
                  const teacherRecords = monthlyData[teacher.id] || {};
                  const recordsArr = Object.values(teacherRecords).map(r => r.status);
                  
                  const daysInMonth = getDaysArray();
                  const todayStr = new Date().toISOString().split('T')[0];
                  
                  let presentCount = recordsArr.filter(v => v === "Present").length;
                  let halfCount = recordsArr.filter(v => v === "Half Day").length;
                  let absentCount = 0;

                  daysInMonth.forEach(d => {
                    if (d <= todayStr) {
                      const st = teacherRecords[d]?.status;
                      if (st === "Absent" || !st) absentCount++; 
                    }
                  });

                  return (
                    <TableRow key={teacher.id} hover>
                      <TableCell sx={{ position: 'sticky', left: 0, bgcolor: "#fff", zIndex: 1, borderRight: '2px solid #e2e8f0' }}>
                        <Typography variant="body2" fontWeight="700" color="#0f172a">{teacher.name}</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ bgcolor: "#ecfdf550", WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}><Typography variant="body2" fontWeight="800" color="#10b981">{presentCount}</Typography></TableCell>
                      <TableCell align="center" sx={{ bgcolor: "#fef2f250", WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}><Typography variant="body2" fontWeight="800" color="#ef4444">{absentCount}</Typography></TableCell>
                      <TableCell align="center" sx={{ bgcolor: "#fffbeb50", WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}><Typography variant="body2" fontWeight="800" color="#f59e0b">{halfCount}</Typography></TableCell>
                      
                      {daysInMonth.map(date => {
                        let status = teacherRecords[date]?.status;
                        
                        if (!status && date <= todayStr) status = "Absent";
                        
                        const config = getStatusConfig(status);

                        return (
                          <TableCell key={date} align="center" sx={{ borderLeft: '1px solid #f1f5f9', p: 0.5 }}>
                            <Box sx={{ 
                              width: 26, height: 26, borderRadius: '50%', 
                              bgcolor: config.bg, color: config.hex, 
                              display: 'flex', alignItems: 'center', justifyContent: 'center', 
                              margin: 'auto', fontWeight: '800', fontSize: '0.75rem',
                              WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact'
                            }}>
                              {config.label}
                            </Box>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* IMAGE VIEWER MODAL */}
      <Dialog className="no-print" open={!!imagePreview} onClose={() => setImagePreview(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, bgcolor: '#000' } }}>
        <Box sx={{ position: 'relative', p: 1 }}>
          <IconButton 
            size="small" 
            onClick={() => setImagePreview(null)} 
            sx={{ position: 'absolute', top: 12, right: 12, bgcolor: 'rgba(255,255,255,0.9)', zIndex: 10, '&:hover': { bgcolor: '#fff' } }}
          >
            <CloseIcon fontSize="small" color="error" />
          </IconButton>
          {imagePreview && (
            <img src={imagePreview} alt="Punch Snapshot" style={{ width: '100%', height: 'auto', borderRadius: '8px', display: 'block' }} />
          )}
        </Box>
      </Dialog>

    </Box>
  );
}

export default Teacherattendance;